import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Download, Lock, LockOpen } from 'lucide-react'
import {
  ETICHETE_TIP_COLABORARE,
  lunaInCuvinteColaborare,
  lunaPrecedenta,
  type RandRaport,
} from '@b4l/shared'
import { api } from '../../../lib/api'
import { useAuthStore } from '../../../stores/auth'
import { downloadXlsx } from '../../../lib/downloadXlsx'
import { Button } from '../../../components/ui/Button'
import { Card } from '../../../components/ui/Card'
import { CardCifra } from '../../../components/ui/CardCifra'
import { Pastila } from '../../../components/ui/Pastila'
import { StareLista } from '../../../components/ui/StareLista'
import { useConfirmare } from '../../../components/ui/Confirmare'
import { dataRo, eroare, lei, lunaDeAzi, type RaportResponsabil } from './comun'

/** Ultimele 18 luni, cea curenta prima. */
function luniDeAles(): string[] {
  const out = [lunaDeAzi()]
  while (out.length < 18) out.push(lunaPrecedenta(out.at(-1)!))
  return out
}

/**
 * Raportul lunar al colaborarilor: contractele noi si colaborarile active cu si fara activitate.
 * Lunile fara inregistrare intra, neutru, la „fara activitate".
 */
export function FilaRaport({
  valideaza,
  responsabilId,
  onDeschide,
}: {
  /** E in lista validatorilor (valideaza si deblocheaza lunile). */
  valideaza: boolean
  responsabilId: number | null
  onDeschide: (id: number) => void
}) {
  const qc = useQueryClient()
  const { confirma, cere } = useConfirmare()
  // Propria luna o valideaza altcineva din lista (regula e pe server); butonul nu se arata.
  const eu = useAuthStore((s) => s.user?.id)
  // Implicit luna trecuta: e cea care se valideaza.
  const [luna, setLuna] = useState(() => lunaPrecedenta(lunaDeAzi()))
  const params = { luna, ...(responsabilId ? { responsabilId } : {}) }

  const q = useQuery({
    queryKey: ['colaborari', 'raport', luna, responsabilId],
    queryFn: async () =>
      (await api.get<{ raport: RaportResponsabil[] }>('/colaborari/raport', { params })).data
        .raport,
  })

  const valideazaLuna = useMutation({
    mutationFn: async (rid: number) =>
      api.post('/colaborari/validari', { luna, responsabilId: rid }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['colaborari'] })
      toast.success('Luna a fost validată și nu se mai schimbă.')
    },
    onError: (e) => toast.error(eroare(e), { duration: 10000 }),
  })
  const deblocheaza = useMutation({
    mutationFn: async (b: { rid: number; motiv: string }) =>
      api.delete('/colaborari/validari', { data: { luna, responsabilId: b.rid, motiv: b.motiv } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['colaborari'] })
      toast.success('Luna a fost deblocată.')
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const exporta = () => {
    const qs = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)]))
    downloadXlsx(`/colaborari/raport/export?${qs}`, `colaborari-${luna}.xlsx`).catch((e: Error) =>
      toast.error(e.message || 'Exportul n-a mers.'),
    )
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={luna}
          onChange={(e) => setLuna(e.target.value)}
          className="rounded border border-b4l-border bg-b4l-surface px-3 py-2 text-sm capitalize"
        >
          {luniDeAles().map((l) => (
            <option key={l} value={l}>
              {lunaInCuvinteColaborare(l)}
            </option>
          ))}
        </select>
        <Button variant="secondary" onClick={exporta}>
          <Download size={14} className="mr-1 inline" /> Export Excel
        </Button>
      </div>

      <StareLista
        interogare={q}
        gol={!q.data?.length}
        mesajGol="Nicio colaborare în evidență pentru luna asta."
      >
        {(q.data ?? []).map((p) => {
          const i = p.raport.indicatori
          return (
            <Card
              key={p.responsabil.id}
              title={`${p.responsabil.name} — ${lunaInCuvinteColaborare(luna)}`}
              actions={
                <div className="flex items-center gap-2">
                  {p.validare ? (
                    <Pastila ton="verde">
                      <Lock size={11} className="mr-1 inline" />
                      Validată de {p.validare.validataDe},{' '}
                      {dataRo(p.validare.validataLa.slice(0, 10))}
                    </Pastila>
                  ) : (
                    <Pastila ton="galben">Nevalidată — cifre provizorii</Pastila>
                  )}
                  {valideaza && !p.validare && luna < lunaDeAzi() && p.responsabil.id !== eu && (
                    <Button
                      className="!py-1 !text-xs"
                      loading={valideazaLuna.isPending}
                      onClick={async () => {
                        if (
                          await confirma({
                            titlu: `Validezi ${lunaInCuvinteColaborare(luna)} pentru ${p.responsabil.name}?`,
                            mesaj:
                              'Raportul de acum se păstrează așa cum e. Lunile fără înregistrare rămân „fără activitate”. Lunile, semnările și stările din luna asta nu se mai pot schimba până la deblocare.',
                            buton: 'Validează luna',
                          })
                        )
                          valideazaLuna.mutate(p.responsabil.id)
                      }}
                    >
                      Validează luna
                    </Button>
                  )}
                  {valideaza && p.validare && (
                    <Button
                      variant="secondary"
                      className="!py-1 !text-xs"
                      onClick={async () => {
                        const motiv = await cere({
                          titlu: 'De ce deblochezi luna?',
                          eticheta: 'Motiv',
                          obligatoriu: true,
                          buton: 'Deblochează',
                        })
                        if (motiv) deblocheaza.mutate({ rid: p.responsabil.id, motiv })
                      }}
                    >
                      <LockOpen size={12} className="mr-1 inline" /> Deblochează
                    </Button>
                  )}
                </div>
              }
            >
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <CardCifra
                  ton="mov"
                  eticheta="Contracte noi"
                  valoare={i.contracteNoi}
                  detaliu={i.valoareContracteNoi ? lei(i.valoareContracteNoi) : undefined}
                />
                <CardCifra
                  ton="verde"
                  eticheta="Active cu activitate"
                  valoare={i.activeCuActivitate}
                />
                <CardCifra
                  ton="gri"
                  eticheta="Active fără activitate"
                  valoare={i.activeFaraActivitate}
                />
                <CardCifra
                  ton="gri"
                  eticheta="Fără înregistrare"
                  valoare={i.faraInregistrare}
                  explicatie="Din cele fără activitate: colaborări active în lună la care nu s-a notat nimic."
                />
              </div>
              <Lista
                titlu="Contracte noi semnate"
                randuri={p.raport.contracteNoi}
                cuSemnare
                onDeschide={onDeschide}
              />
              <Lista
                titlu="Cu activitate"
                randuri={p.raport.cuActivitate}
                cuDescriere
                onDeschide={onDeschide}
              />
              <Lista
                titlu="Fără activitate"
                randuri={p.raport.faraActivitate}
                onDeschide={onDeschide}
              />
            </Card>
          )
        })}
      </StareLista>
    </div>
  )
}

function Lista({
  titlu,
  randuri,
  cuSemnare,
  cuDescriere,
  onDeschide,
}: {
  titlu: string
  randuri: RandRaport[]
  cuSemnare?: boolean
  cuDescriere?: boolean
  onDeschide: (id: number) => void
}) {
  if (!randuri.length) return null
  return (
    <div className="mt-4">
      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
        {titlu} ({randuri.length})
      </h4>
      <ul className="divide-y divide-b4l-border text-sm">
        {randuri.map((r) => (
          <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 py-1.5">
            <button
              type="button"
              className="text-left font-medium hover:underline"
              onClick={() => onDeschide(r.id)}
            >
              {r.denumire}
              <span className="ml-2 text-xs font-normal text-b4l-text-muted">
                {ETICHETE_TIP_COLABORARE[r.tip]}
              </span>
            </button>
            <span className="text-b4l-text-muted">
              {cuSemnare &&
                `semnat ${dataRo(r.dataSemnare)}${r.valoare ? ` · ${lei(r.valoare)}` : ''}`}
              {cuDescriere && r.descriere}
              {!cuSemnare &&
                !cuDescriere &&
                (r.faraInregistrare ? 'fără înregistrare' : r.descriere)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
